import React, { useMemo, useState } from "react";
import { GlassWater, Loader2, Store } from "lucide-react";
import ReactSelect, { SingleValue, StylesConfig } from "react-select";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns-tz";

import PageHeader from "../../../components/PageHeader";
import { SELECT_STYLES } from "../../../components/DateFilters/constants";
import { BrazilDate } from "../../../utils/BrazilDate";
import { lojaService } from "../../../services/lojaService";
import { fetchRelatorioSalaoBebidas } from "../../../services/pedidoIntegracaoService";

type LojaOpt = { value: number; label: string };

const LOJA_SELECT_STYLES = SELECT_STYLES as unknown as StylesConfig<
  LojaOpt,
  false
>;

function getDefaultMonthRange() {
  const now = new Date();
  const tz = BrazilDate.getTimezone();
  const yearStr = format(now, "yyyy", { timeZone: tz });
  const monthStr = format(now, "MM", { timeZone: tz });
  const year = parseInt(yearStr, 10);
  const month = parseInt(monthStr, 10);
  const firstDay = BrazilDate.create(year, month, 1);
  const nextMonth = month === 12 ? 1 : month + 1;
  const nextYear = month === 12 ? year + 1 : year;
  const firstDayNextMonth = BrazilDate.create(nextYear, nextMonth, 1);
  const lastDay = new Date(firstDayNextMonth.getTime() - 24 * 60 * 60 * 1000);
  return {
    dataInicio: BrazilDate.formatISO(firstDay),
    dataFim: BrazilDate.formatISO(lastDay),
  };
}

const DEFAULT_MONTH = getDefaultMonthRange();

function formatQty(n: number): string {
  return n.toLocaleString("pt-BR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

const SalaoBebidas: React.FC = () => {
  const [startDate, setStartDate] = useState(DEFAULT_MONTH.dataInicio);
  const [endDate, setEndDate] = useState(DEFAULT_MONTH.dataFim);
  const [lojaId, setLojaId] = useState<number | null>(null);
  const [applied, setApplied] = useState<null | {
    dataInicio: string;
    dataFim: string;
    lojaId: number | null;
  }>(null);

  const { data: lojas = [] } = useQuery({
    queryKey: ["lojas"],
    queryFn: () => lojaService.getLojas(),
  });

  const lojaOptions = useMemo(
    () => lojas.map((l) => ({ value: l.id, label: l.nome })),
    [lojas]
  );
  const lojaSel = lojaOptions.find((o) => o.value === (lojaId ?? -1)) ?? null;

  const { data, isFetching, refetch } = useQuery({
    queryKey: [
      "relatorio-salao-bebidas",
      applied?.dataInicio ?? "no-applied",
      applied?.dataFim ?? "no-applied",
      applied?.lojaId ?? "no-applied",
    ],
    enabled: applied != null,
    queryFn: () =>
      fetchRelatorioSalaoBebidas({
        dataInicio: applied!.dataInicio,
        dataFim: applied!.dataFim,
        lojaId: applied!.lojaId,
      }),
  });

  const handleAplicar = () => {
    setApplied({
      dataInicio: startDate,
      dataFim: endDate,
      lojaId,
    });
  };

  const pIni =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataInicio), false)
      : null;
  const pFim =
    applied != null
      ? BrazilDate.formatBR(BrazilDate.fromISOString(applied.dataFim), false)
      : null;
  const lojaLabel =
    applied != null
      ? applied.lojaId != null
        ? (lojaOptions.find((o) => o.value === applied.lojaId)?.label ?? "—")
        : "Todas"
      : null;

  const porBebida = data?.porBebida ?? [];
  const linhas = data?.linhas ?? [];
  const total = data?.totalBebidas ?? 0;

  return (
    <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader title="Bebidas Salão" description="" />

      <div className="mb-6 rounded-lg border border-neutral-200 bg-neutral-50/80 p-4">
        <p className="mb-3 text-sm font-medium text-neutral-800">Filtros</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="salao-bebidas-ini"
              className="text-xs font-medium text-neutral-700"
            >
              Data início
            </label>
            <input
              id="salao-bebidas-ini"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="salao-bebidas-fim"
              className="text-xs font-medium text-neutral-700"
            >
              Data fim
            </label>
            <input
              id="salao-bebidas-fim"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-neutral-700">Loja</span>
            <ReactSelect<LojaOpt, false>
              inputId="salao-bebidas-loja"
              value={lojaSel}
              onChange={(o: SingleValue<LojaOpt>) =>
                setLojaId(o?.value ?? null)
              }
              options={lojaOptions}
              isClearable
              placeholder="Todas"
              styles={LOJA_SELECT_STYLES}
              className="text-sm"
              noOptionsMessage={() => "Nenhuma loja"}
            />
          </div>
        </div>
        <button
          type="button"
          onClick={handleAplicar}
          className="mt-4 inline-flex rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-90"
        >
          Aplicar filtros
        </button>
      </div>

      {applied != null ? (
        <p className="mb-4 text-sm text-neutral-600">
          Período: <span className="font-medium text-neutral-800">{pIni}</span>{" "}
          a <span className="font-medium text-neutral-800">{pFim}</span>
          {" · "}
          Loja:{" "}
          <span className="font-medium text-neutral-800">{lojaLabel}</span>
          {" · "}
          Total:{" "}
          <span className="font-medium text-neutral-800">
            {formatQty(total)}
          </span>
        </p>
      ) : (
        <p className="mb-4 text-sm text-neutral-600">
          Selecione o período e clique em <strong>Aplicar filtros</strong>.
        </p>
      )}

      <div className="relative">
        {applied != null && isFetching && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/60">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {applied != null ? (
          <div className="space-y-8">
            <section className="space-y-6 rounded-2xl border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
              <div className="flex items-start gap-3 border-b border-neutral-100 pb-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Store size={22} strokeWidth={2} />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base font-semibold text-neutral-900">
                    Salão (Foodfly)
                  </h2>
                  <p className="mt-0.5 text-sm text-neutral-600">
                    Bebidas finalizadas na fila do salão — mesma base do KPI de
                    bebidas do dashboard.
                  </p>
                  <p className="mt-2 text-sm text-neutral-700">
                    Total:{" "}
                    <span className="font-semibold tabular-nums text-neutral-900">
                      {formatQty(total)}
                    </span>
                  </p>
                </div>
              </div>

              <div>
                <h3 className="mb-3 text-sm font-semibold text-neutral-800">
                  Por bebida
                </h3>
                {porBebida.length === 0 ? (
                  <div className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
                    Nenhuma bebida nesta seção no período.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-neutral-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-neutral-200 bg-neutral-50">
                        <tr>
                          <th className="px-4 py-3 font-semibold text-neutral-800">
                            Bebida
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                            Qtd.
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {porBebida.map((row) => (
                          <tr
                            key={row.bebidaNome}
                            className="border-b border-neutral-100 last:border-0"
                          >
                            <td className="px-4 py-2.5 text-neutral-900">
                              {row.bebidaNome}
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                              {formatQty(row.quantidade)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                        <tr>
                          <td className="px-4 py-2.5 text-sm font-semibold text-neutral-800">
                            Total
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                            {formatQty(total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <h3 className="mb-3 text-sm font-semibold text-neutral-800">
                  Por loja e bebida
                </h3>
                {linhas.length === 0 ? (
                  <div className="rounded-lg border border-neutral-100 bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">
                    Nenhuma linha encontrada nesta seção.
                  </div>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-neutral-200">
                    <table className="min-w-full text-left text-sm">
                      <thead className="border-b border-neutral-200 bg-neutral-50">
                        <tr>
                          <th className="px-4 py-3 font-semibold text-neutral-800">
                            Loja
                          </th>
                          <th className="px-4 py-3 font-semibold text-neutral-800">
                            Bebida
                          </th>
                          <th className="px-4 py-3 text-right font-semibold text-neutral-800">
                            Qtd.
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {linhas.map((row) => (
                          <tr
                            key={`${row.lojaNome}|${row.bebidaNome}`}
                            className="border-b border-neutral-100 last:border-0"
                          >
                            <td className="px-4 py-2.5 text-neutral-900">
                              {row.lojaNome}
                            </td>
                            <td className="px-4 py-2.5 text-neutral-900">
                              {row.bebidaNome}
                            </td>
                            <td className="px-4 py-2.5 text-right font-medium tabular-nums text-neutral-900">
                              {formatQty(row.quantidade)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="border-t-2 border-neutral-200 bg-neutral-50/90">
                        <tr>
                          <td
                            colSpan={2}
                            className="px-4 py-2.5 text-sm font-semibold text-neutral-800"
                          >
                            Total
                          </td>
                          <td className="px-4 py-2.5 text-right text-sm font-bold tabular-nums text-neutral-900">
                            {formatQty(total)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </div>
            </section>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm text-neutral-700">
              <span className="inline-flex items-center gap-2 font-medium text-neutral-900">
                <GlassWater size={16} aria-hidden />
                Total geral
              </span>
              <span className="ml-2 tabular-nums font-bold text-neutral-900">
                {formatQty(total)}
              </span>
            </div>
          </div>
        ) : null}

        {applied != null ? (
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-6 text-sm font-medium text-primary hover:underline"
          >
            Recarregar dados
          </button>
        ) : null}
      </div>
    </main>
  );
};

export default SalaoBebidas;
